/**
 * Web chat tool-card for `novaCompile` (M4.3): renders the compiled Nova plan
 * the agent produced: NL summary + "when it runs" + the ordered steps with their
 * dry_run / requires_approval safety flags. Read-only (the user approves from the
 * Nova board / builder). Web-only; a `.native.tsx` sibling renders iOS/Android.
 */
import React, { type FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { NovaStepPreview } from '@tabbio/types';
import { MaterialSymbol } from '../../icons/MaterialSymbol';
import { CardShell } from './card-shell';
import { safeGet } from './types';
import type { ToolCardProps } from './types';

export const NovaPlanCard: FC<ToolCardProps> = ({ result }) => {
  const { t } = useTranslation('common');

  const summary =
    safeGet<string>(result, 'summary') ??
    safeGet<string>(result, 'dsl', 'summary') ??
    '';
  const triggerDescription = safeGet<string>(result, 'structuredPreview', 'trigger', 'description');
  const rawSteps = safeGet<NovaStepPreview[]>(result, 'structuredPreview', 'steps');
  const steps = Array.isArray(rawSteps) ? rawSteps : [];

  return (
    <CardShell>
      <div style={{ padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <MaterialSymbol name="conversion_path" size={15} style={{ color: '#5e29ff' }} />
          <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-tertiary)' }}>
            {t('nova.card.planLabel')}
          </span>
        </div>

        {summary ? (
          <p style={{ margin: 0, fontSize: 15, lineHeight: '21px', color: 'var(--text-primary)' }} dir="auto">
            {summary}
          </p>
        ) : null}

        {triggerDescription ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--text-secondary)' }}>
            <MaterialSymbol name="schedule" size={15} style={{ color: 'var(--text-tertiary)' }} />
            <span dir="auto">{triggerDescription}</span>
          </div>
        ) : null}

        {steps.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-tertiary)' }}>
              {t('nova.card.stepsLabel')}
            </span>
            {steps.slice(0, 5).map((step, i) => (
              <div
                key={step.id ?? i}
                style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: 'var(--text-secondary)' }}
              >
                <MaterialSymbol
                  name={step.dryRun ? 'edit_note' : step.requiresApproval ? 'gpp_maybe' : 'bolt'}
                  size={15}
                  style={{ color: 'var(--text-tertiary)' }}
                />
                <span dir="auto" style={{ flex: 1, minWidth: 0 }}>{step.label || step.type}</span>
                {step.dryRun ? <span className="tabbio-menu-badge">{t('nova.card.draftBadge')}</span> : null}
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </CardShell>
  );
};
